import {
  Badge,
  Box,
  Button,
  Flex,
  Icon,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon, SearchIcon } from '@chakra-ui/icons';
import React, { useEffect, useRef } from 'react';
import { FaCopy, FaImages, FaPen, FaTrash, FaUpload } from 'react-icons/fa';
import Swal from 'sweetalert2';
import Card from 'components/card/Card';
import { useUploadImageMutation } from 'api/fileUploadSlice';
import {
  useDeleteMediaMutation,
  useGetMediaQuery,
  useUpdateMediaTitleMutation,
} from 'api/mediaSlice';

const PER_PAGE = 20;

const formatBytes = (bytes) => {
  const size = Number(bytes) || 0;
  if (!size) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const MediaThumb = ({ src, alt, height = '160px', objectFit = 'cover' }) => (
  <Box w="100%" h={height} bg="gray.100" overflow="hidden">
    <Image
      src={src}
      alt={alt}
      w="100%"
      h={height}
      objectFit={objectFit}
      fallback={
        <Flex w="100%" h={height} align="center" justify="center">
          <Icon as={FaImages} color="gray.400" boxSize="28px" />
        </Flex>
      }
    />
  </Box>
);

const MediaShimmerGrid = () => (
  <SimpleGrid columns={{ base: 1, sm: 2, md: 4, xl: 5 }} spacing={5}>
    {Array.from({ length: PER_PAGE }).map((_, index) => (
      <Box key={index} borderRadius="16px" overflow="hidden" boxShadow="sm">
        <Skeleton h="160px" w="100%" startColor="gray.100" endColor="gray.300" />
        <Box p="14px">
          <Skeleton h="14px" w="80%" mb="8px" borderRadius="full" />
          <Skeleton h="10px" w="50%" borderRadius="full" />
        </Box>
      </Box>
    ))}
  </SimpleGrid>
);

const MediaLibrary = () => {
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageLoading, setPageLoading] = React.useState(true);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [editTitle, setEditTitle] = React.useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const requestKey = `${currentPage}|${searchQuery}`;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const searchBg = useColorModeValue('secondaryGray.300', 'gray.700');
  const searchColor = useColorModeValue('gray.700', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const muted = useColorModeValue('gray.500', 'gray.400');
  const hoverBorder = useColorModeValue('brand.400', 'brand.300');

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = searchInput.trim();
      if (next === searchQuery) return;
      setSearchQuery(next);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, searchQuery]);

  useEffect(() => {
    setPageLoading(true);
  }, [requestKey]);

  const { data, isLoading, isError, isFetching, refetch } = useGetMediaQuery(
    { search: searchQuery, page: currentPage, per_page: PER_PAGE },
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (!isFetching && !isLoading) {
      setPageLoading(false);
    }
  }, [isFetching, isLoading, requestKey, data]);
  const [uploadImage, { isLoading: isUploading }] = useUploadImageMutation();
  const [updateMediaTitle, { isLoading: isSavingTitle }] = useUpdateMediaTitleMutation();
  const [deleteMedia] = useDeleteMediaMutation();

  const items = Array.isArray(data?.data) ? data.data : [];
  const pagination = data?.pagination || {};
  const total = pagination.total || 0;
  const lastPage = pagination.last_page || 1;
  const from = pagination.from || (items.length ? (currentPage - 1) * PER_PAGE + 1 : 0);
  const to = pagination.to || (items.length ? from + items.length - 1 : 0);
  const showShimmer = isLoading || pageLoading;

  const openPreview = (item) => {
    setSelectedItem(item);
    setEditTitle(item.title || '');
    onOpen();
  };

  const copyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: 'Copied',
        description: 'Image URL copied to clipboard',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch {
      toast({ title: 'Could not copy URL', status: 'error', duration: 2500, isClosable: true });
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    try {
      const response = await uploadImage({ file }).unwrap();
      if (!response?.success) throw new Error('Upload failed');
      toast({
        title: 'Uploaded',
        description: 'Image added to the media library',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setCurrentPage(1);
      refetch();
    } catch (error) {
      toast({
        title: 'Upload failed',
        description: error?.data?.message || error.message || 'Could not upload image',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleSaveTitle = async () => {
    if (!selectedItem || !editTitle.trim()) return;
    try {
      await updateMediaTitle({
        filename: selectedItem.filename || selectedItem.id,
        title: editTitle.trim(),
      }).unwrap();
      setSelectedItem((prev) => (prev ? { ...prev, title: editTitle.trim() } : prev));
      toast({ title: 'Title updated', status: 'success', duration: 2500, isClosable: true });
    } catch (error) {
      toast({
        title: 'Could not update title',
        description: error?.data?.message || 'Please try again',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Delete this image?',
      text: item.title || item.filename,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53e3e',
      confirmButtonText: 'Delete',
    });
    if (!result.isConfirmed) return;

    try {
      await deleteMedia(item.filename || item.id).unwrap();
      if (selectedItem && selectedItem.id === item.id) onClose();
      toast({ title: 'Deleted', status: 'success', duration: 2500, isClosable: true });
    } catch (error) {
      toast({
        title: 'Could not delete image',
        description: error?.data?.message || 'This image may still be in use',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    }
  };

  return (
    <Box className="nr-page">
      <Card flexDirection="column" w="100%" px="0px">
        <Flex
          px={{ base: '16px', md: '25px' }}
          mb="20px"
          direction={{ base: 'column', md: 'row' }}
          justifyContent="space-between"
          align={{ base: 'stretch', md: 'center' }}
          gap={4}
        >
          <Box>
            <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
              Media Library
            </Text>
            <Text color={muted} fontSize="sm" mt="6px">
              {total ? `${total} images` : 'All uploaded and used images'}
            </Text>
          </Box>

          <InputGroup w={{ base: '100%', md: '380px' }}>
            <InputLeftElement>
              <IconButton
                bg="inherit"
                borderRadius="inherit"
                _hover="none"
                icon={<SearchIcon w="15px" h="15px" />}
              />
            </InputLeftElement>
            <Input
              variant="search"
              fontSize="sm"
              bg={searchBg}
              color={searchColor}
              fontWeight="500"
              _placeholder={{ color: 'gray.400', fontSize: '14px' }}
              borderRadius="30px"
              placeholder="Search by title or file name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </InputGroup>

          <Button
            variant="darkBrand"
            color="white"
            fontSize="sm"
            fontWeight="500"
            borderRadius="70px"
            px="24px"
            leftIcon={<Icon as={FaUpload} />}
            isLoading={isUploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload image
          </Button>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            display="none"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              handleUpload(file);
            }}
          />
        </Flex>

        <Box px={{ base: '16px', md: '25px' }} pb="24px">
          {showShimmer ? (
            <MediaShimmerGrid />
          ) : isError ? (
            <Flex justify="center" align="center" minH="220px">
              <Text color="red.500">Could not load the media library.</Text>
            </Flex>
          ) : items.length === 0 ? (
            <Flex direction="column" justify="center" align="center" minH="220px" gap={3}>
              <Icon as={FaImages} boxSize="36px" color={muted} />
              <Text color={muted}>
                {searchQuery ? 'No images match your search.' : 'No images uploaded yet.'}
              </Text>
            </Flex>
          ) : (
            <SimpleGrid columns={{ base: 1, sm: 2, md: 4, xl: 5 }} spacing={5}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  bg={cardBg}
                  borderWidth="1px"
                  borderColor="transparent"
                  borderRadius="16px"
                  overflow="hidden"
                  boxShadow="sm"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{ borderColor: hoverBorder, transform: 'translateY(-2px)' }}
                  onClick={() => openPreview(item)}
                >
                  <MediaThumb src={item.url} alt={item.title} />
                  <Box p="14px">
                    <Text color={textColor} fontWeight="700" fontSize="sm" noOfLines={1}>
                      {item.title || 'Untitled'}
                    </Text>
                    <Text color={muted} fontSize="xs" mt="4px" noOfLines={1}>
                      {(item.used_in || []).length > 0
                        ? `Used in ${(item.used_in || [])[0].type}${(item.used_in || []).length > 1 ? ` +${(item.used_in || []).length - 1}` : ''}`
                        : formatBytes(item.size) || 'Uploaded file'}
                    </Text>
                    <Flex mt="8px" wrap="wrap" gap={1}>
                      {(item.used_in || []).slice(0, 2).map((usage, index) => (
                        <Badge key={`${item.id}-${usage.type}-${index}`} colorScheme="green" borderRadius="full">
                          {usage.type}
                        </Badge>
                      ))}
                    </Flex>
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          )}

          {total > 0 && (
            <Flex justify="space-between" align="center" mt="24px" wrap="wrap" gap={3}>
              <Text fontSize="sm" color={muted}>
                Showing {from}-{to} of {total}
              </Text>
              <Flex align="center" gap={3}>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  size="sm"
                  isDisabled={currentPage <= 1 || isFetching}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                />
                <Text fontSize="sm" color={textColor}>
                  Page {currentPage} of {lastPage}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  size="sm"
                  isDisabled={currentPage >= lastPage || isFetching}
                  onClick={() => setCurrentPage((page) => Math.min(lastPage, page + 1))}
                />
              </Flex>
            </Flex>
          )}
        </Box>
      </Card>

      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Image details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedItem && (
              <VStack align="stretch" spacing={4}>
                <Box borderRadius="12px" overflow="hidden">
                  <MediaThumb
                    src={selectedItem.url}
                    alt={selectedItem.title}
                    height="360px"
                    objectFit="contain"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="600" mb="6px">
                    Title
                  </Text>
                  <Input
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Image title"
                  />
                </Box>
                <Text fontSize="sm" color={muted} wordBreak="break-all">
                  {selectedItem.url}
                </Text>
                {(selectedItem.used_in || []).length > 0 && (
                  <Flex wrap="wrap" gap={2}>
                    {(selectedItem.used_in || []).map((usage, index) => (
                      <Badge key={`${usage.type}-${usage.id}-${index}`} colorScheme="blue">
                        {usage.type}: {usage.name}
                      </Badge>
                    ))}
                  </Flex>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              leftIcon={<FaCopy />}
              variant="outline"
              onClick={() => selectedItem && copyUrl(selectedItem.url)}
            >
              Copy URL
            </Button>
            <Button
              leftIcon={<FaPen />}
              colorScheme="blue"
              isLoading={isSavingTitle}
              onClick={handleSaveTitle}
            >
              Save title
            </Button>
            <Button
              leftIcon={<FaTrash />}
              colorScheme="red"
              variant="ghost"
              onClick={() => selectedItem && handleDelete(selectedItem)}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default MediaLibrary;
